import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Search,
  Music,
  Clock,
  Users,
  Mic,
  Play,
  Heart
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { subscribeQueueUpdate } from '@/lib/queueSignal';
import Footer from "@/components/Footer";

interface Song {
  id: string;
  title: string;
  artist: string;
  genre: string;
}

interface QueueRequest {
  id: string;
  song_id: string;
  singer_name: string;
  status: string;
  queue_position: number;
  created_at: string;
  custom_song_title?: string;
  custom_song_artist?: string;
  songs: {
    title: string;
    artist: string;
  };
}

const SingerPage = ({ demo = false, demoVenueSlug = 'demo' }: { demo?: boolean; demoVenueSlug?: string }) => {
  const { toast } = useToast();
  const { venue_slug } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [singerName, setSingerName] = useState('');
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [songs, setSongs] = useState<Song[]>([]);
  const [queue, setQueue] = useState<QueueRequest[]>([]);
  const [venueId, setVenueId] = useState<string | null>(null);
  const [venueName, setVenueName] = useState('Loading venue...');
  const [venue, setVenue] = useState<any>(null);
  const [hostSubscriptionLevel, setHostSubscriptionLevel] = useState<string>('basic');

  const [submitting, setSubmitting] = useState(false);
  const [isCustomRequestOpen, setIsCustomRequestOpen] = useState(false);
  const [customSongTitle, setCustomSongTitle] = useState('');
  const [customSongArtist, setCustomSongArtist] = useState('');
  const [customSingerName, setCustomSingerName] = useState('');
  const [showBanner, setShowBanner] = useState(true);
  const [showDemoBanner, setShowDemoBanner] = useState(true);
  const [displayedSongsCount, setDisplayedSongsCount] = useState(50); // Virtual scrolling: show 50 songs at a time

  const isDemo = demo || venue_slug === demoVenueSlug;

  // Tier restrictions
  const isEnterprise = hostSubscriptionLevel === 'enterprise';
  const isRegular = hostSubscriptionLevel === 'regular';
  const isBasic = hostSubscriptionLevel === 'basic';

  useEffect(() => {
    console.log('SingerPage mounted, venue_slug:', venue_slug, 'isDemo:', isDemo);
    loadVenueData();

    // Subscribe to centralized queue signals (both cross-tab and same-tab)
    const unsubscribe = subscribeQueueUpdate((detail) => {
      // If payload contains an action and requestId we can do a lightweight update,
      // otherwise reload the full queue for consistency.
      const action = detail?.action;
      const requestId = detail?.requestId || detail?.songId;
      if (venueId && (action === 'removed' || action === 'completed') && requestId) {
        // remove from local queue immediately
        setQueue((prev) => (prev || []).filter((q) => q.id !== requestId));
      } else if (venueId && (action === 'demo-added' || action === 'demo-custom-added')) {
        // demo actions are handled locally elsewhere; ensure we refresh to stay consistent
        loadQueue(venueId);
      } else if (venueId && (action === 'added' || action === 'approved' || action === 'moved' || action === 'reordered' || action === 'cleared')) {
        loadQueue(venueId);
      } else {
        // fallback: reload
        if (venueId) loadQueue(venueId);
      }
    });

    return () => unsubscribe();
  }, [venue_slug, venueId]);

  const loadVenueData = async () => {
    try {

  // Demo mode: populate local demo data and skip network calls
  if (isDemo) {
        const demoId = 'demo-venue-id';
        setVenueId(demoId);
        setVenueName('Demo Lounge');
        setVenue({
          id: demoId,
          name: 'Demo Lounge',
          allow_requests: true,
          logo_url: null,
          primary_color: '#7c3aed',
          secondary_color: '#06b6d4',
        });

        // small demo song list
        const demoSongs = [
          { id: 's1', title: 'Bohemian Rhapsody', artist: 'Queen', genre: 'Rock' },
          { id: 's2', title: 'Dancing Queen', artist: 'ABBA', genre: 'Pop' },
          { id: 's3', title: 'Sweet Caroline', artist: 'Neil Diamond', genre: 'Classic' },
        ];
        setSongs(demoSongs);

        // small demo queue
          setQueue([
          { id: 'q1', song_id: 's1', singer_name: 'Demo Singer', status: 'playing', queue_position: 1, created_at: new Date().toISOString(), songs: { title: 'Bohemian Rhapsody', artist: 'Queen' } },
          { id: 'q2', song_id: 's2', singer_name: 'Alex', status: 'queued', queue_position: 2, created_at: new Date().toISOString(), songs: { title: 'Dancing Queen', artist: 'ABBA' } },
        ]);

        return;
      }

      // Load venue by slug
      const { data: venueData, error: venueError } = await supabase
        .from('venues')
        .select('*')
        .eq('slug', venue_slug)
        .single();

      if (venueError) {
        console.error('Error loading venue:', venueError);
        toast({
          title: "Error",
          description: "Could not load venue information",
          variant: "destructive"
        });
        return;
      }

      setVenueId(venueData.id);
      setVenueName(venueData.name);
      setVenue(venueData);

      // Load host subscription level to enforce tier restrictions.
      // The anon key can no longer read the hosts table directly (it was locked
      // down to stop exposing password_hash), so go through a SECURITY DEFINER
      // RPC that returns only the venue's effective tier.
      const { data: tierData, error: tierError } = await (supabase
        .rpc as any)('get_venue_subscription_level', { p_venue_id: venueData.id });

      if (tierError) {
        console.error('Error loading venue subscription level:', tierError);
      } else if (tierData) {
        setHostSubscriptionLevel(tierData as string);
      }
      
      // Load initial songs batch for faster page load (first 500 songs)
      // This allows the page to render quickly while loading more in background
      const { data: initialSongs, error: initialError } = await supabase
        .from('songs')
        .select('id, title, artist, genre')
        .eq('venue_id', venueData.id)
        .eq('is_enabled', true)
        .order('artist', { ascending: true })
        .limit(500);

      if (initialError) {
        console.error('Error loading initial songs:', initialError);
        toast({
          title: "Error",
          description: "Could not load songs",
          variant: "destructive"
        });
      } else {
        console.log('SingerPage: Loaded initial songs count:', initialSongs?.length || 0, 'for venue:', venueData.id);
        setSongs(initialSongs || []);
      }

      // Load remaining songs in background asynchronously (don't block page render)
      const loadRemainingSongs = async () => {
        try {
          let allSongs = initialSongs || [];
          let offset = 500;
          const batchSize = 1000;
          let hasMore = (initialSongs?.length || 0) === 500;

          while (hasMore) {
            const { data: batchData, error: batchError } = await supabase
              .from('songs')
              .select('id, title, artist, genre')
              .eq('venue_id', venueData.id)
              .eq('is_enabled', true)
              .order('artist', { ascending: true })
              .range(offset, offset + batchSize - 1);

            if (batchError) throw batchError;

            if (batchData && batchData.length > 0) {
              allSongs = [...allSongs, ...batchData];
              offset += batchSize;
              hasMore = batchData.length === batchSize;
            } else {
              hasMore = false;
            }
          }

          setSongs(allSongs);
          console.log('Background: Loaded all songs count:', allSongs.length);
        } catch (error) {
          console.error('Error loading remaining songs:', error);
        }
      };

      // Start background loading after initial render (doesn't block UI)
      setTimeout(loadRemainingSongs, 100);
      
      // Load queue
      loadQueue(venueData.id);

      // Load announcements (TODO: Add after types are updated)
      // loadAnnouncements(venueData.id);

    } catch (error) {
      console.error('Error in loadVenueData:', error);
      toast({
        title: "Error",
        description: "Failed to load venue data",
        variant: "destructive"
      });
    } finally {
      // Loading complete
    }
  };

  // TODO: Add announcements loading after types are updated

  const loadQueue = async (venueId: string) => {
    try {
      const { data: queueData, error } = await supabase
        .from('requests')
        .select(`
          *,
          songs (*)
        `)
        .eq('venue_id', venueId)
        .in('status', ['queued', 'playing'])
        .order('queue_position', { ascending: true });

      if (error) {
        console.error('Error loading queue:', error);
        toast({
          title: "Error",
          description: "Could not load queue",
          variant: "destructive"
        });
      } else {
        setQueue(queueData || []);
      }
    } catch (error) {
      console.error('Error in loadQueue:', error);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!venueId) return;

    const channel = supabase
      .channel('singer-page-updates')
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'requests',
          filter: `venue_id=eq.${venueId}`
        },
        () => {
          loadQueue(venueId);
        }
      )
      .on('postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'venues',
          filter: `id=eq.${venueId}`,
        },
        (payload: any) => {
          setVenue((prev: any) => prev ? { ...prev, ...payload.new } : prev);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  // Set up real-time subscription after venue is loaded
  useEffect(() => {
    if (venueId) {
      const cleanup = setupRealtimeSubscription();
      return cleanup;
    }
  }, [venueId]);

  // Optimized filtering for large song libraries
  const filteredSongs = useMemo(() => {
    // Reset displayed count when search changes for better UX
    setDisplayedSongsCount(50);
    
    if (!searchTerm.trim()) return songs;

    const searchLower = searchTerm.toLowerCase();
    return songs.filter(song =>
      song.title.toLowerCase().includes(searchLower) ||
      song.artist.toLowerCase().includes(searchLower) ||
      song.genre.toLowerCase().includes(searchLower)
    );
  }, [songs, searchTerm]);

  const handleRequestSong = async () => {
    if (isDemo) {
      // Simulate adding to queue locally
      const newId = `demo-q-${Date.now()}`;
      const newPosition = (queue?.length || 0) + 1;
      const newReq = {
        id: newId,
        song_id: selectedSong?.id || null,
        singer_name: singerName.trim(),
        status: 'queued',
        queue_position: newPosition,
        created_at: new Date().toISOString(),
        songs: { title: selectedSong?.title || 'Unknown', artist: selectedSong?.artist || '' }
      } as QueueRequest;

      setQueue((prev) => [...(prev || []), newReq]);
      setSingerName('');
      setSelectedSong(null);
      setIsRequestDialogOpen(false);
      toast({ title: 'Demo: Song Requested', description: `${newReq.songs.title} added to demo queue` });

      try {
        // use centralized signaling helper
        const { sendQueueUpdate } = await import('@/lib/queueSignal');
        sendQueueUpdate({ action: 'demo-added', songId: newId });
      } catch (err) {}

      return;
    }
    if (!singerName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter your name to make a request",
        variant: "destructive",
      });
      return;
    }

    // Check if requests are allowed
    if (venue?.allow_requests === false) {
      toast({
        title: "Requests Disabled",
        description: "The host has temporarily disabled new requests. Please try again later.",
        variant: "destructive"
      });
      return;
    }

    if (!selectedSong || !venueId) {
      toast({
        title: "Error",
        description: "Venue not loaded. Please refresh the page.",
        variant: "destructive"
      });
      return;
    }

    try {
      setSubmitting(true);
      
      const { error } = await supabase
        .from('requests')
        .insert({
          song_id: selectedSong.id,
          singer_name: singerName.trim(),
          venue_id: venueId,
          status: 'queued'
        });

      if (error) {
        console.error('Error submitting request:', error);
        toast({
          title: "Error",
          description: "Could not submit your request. Please try again.",
          variant: "destructive"
        });
      } else {
        setSingerName('');
        setSelectedSong(null);
        setIsRequestDialogOpen(false);
        toast({
          title: "Song Requested! 🎤",
          description: `"${selectedSong.title}" by ${selectedSong.artist} has been added to the queue`,
        });
        // Signal other tabs / projection to refresh as a backup to real-time
        try {
          const { sendQueueUpdate } = await import('@/lib/queueSignal');
          sendQueueUpdate({ action: 'added', songId: selectedSong.id });
        } catch (err) {
          // ignore
        }
        // Queue will update automatically via real-time subscription
      }
    } catch (error) {
      console.error('Error in handleRequestSong:', error);
      toast({
        title: "Error",
        description: "Failed to submit request",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const openRequestDialog = (song: Song) => {
    setSelectedSong(song);
    setIsRequestDialogOpen(true);
  };

  const handleCustomRequest = async () => {
    if (isDemo) {
      // Simulate immediate approval and queue insertion
      const newId = `demo-q-${Date.now()}`;
      const newPosition = (queue?.length || 0) + 1;
      const newReq = {
        id: newId,
        song_id: null,
        singer_name: customSingerName.trim(),
        status: 'queued',
        queue_position: newPosition,
        created_at: new Date().toISOString(),
        songs: { title: customSongTitle.trim(), artist: customSongArtist.trim() }
      } as QueueRequest;

      setQueue((prev) => [...(prev || []), newReq]);
      setCustomSongTitle('');
      setCustomSongArtist('');
      setCustomSingerName('');
      setIsCustomRequestOpen(false);
      toast({ title: 'Demo: Custom Song Added', description: `${newReq.songs.title} added to demo queue` });
      try {
        const { sendQueueUpdate } = await import('@/lib/queueSignal');
        sendQueueUpdate({ action: 'demo-custom-added', songId: newId });
      } catch (err) {}
      return;
    }
    if (!customSongTitle.trim() || !customSongArtist.trim() || !customSingerName.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields for your custom request",
        variant: "destructive"
      });
      return;
    }

    // Check if requests are allowed
    if (venue?.allow_requests === false) {
      toast({
        title: "Requests Disabled",
        description: "The host has temporarily disabled new requests. Please try again later.",
        variant: "destructive"
      });
      return;
    }

    try {
      setSubmitting(true);

      // Create the custom song request directly
      const { error } = await supabase
        .from('requests')
        .insert({
          song_id: null, // No song ID for custom requests
          singer_name: customSingerName.trim(),
          venue_id: venueId,
          status: 'pending',
          approval_status: 'pending',
          queue_position: 0, // Will be set when approved
          custom_song_title: customSongTitle.trim(),
          custom_song_artist: customSongArtist.trim()
        });

      if (error) {
        console.error('Error submitting custom request:', error);
        toast({
          title: "Error",
          description: "Could not submit your custom request. Please try again.",
          variant: "destructive"
        });
      } else {
        setCustomSongTitle('');
        setCustomSongArtist('');
        setCustomSingerName('');
        setIsCustomRequestOpen(false);
        toast({
          title: "Custom Song Submitted! 🎤",
          description: `"${customSongTitle}" by ${customSongArtist} is pending host approval`,
        });
      }
    } catch (error) {
      console.error('Error in handleCustomRequest:', error);
      toast({
        title: "Error",
        description: "Failed to submit custom request",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const activeQueue = queue.filter(req => req.status === 'queued');

  return (
    <div
      className="min-h-screen stage-background"
      style={{
        '--venue-primary': venue?.primary_color || '#3b82f6',
        '--venue-secondary': venue?.secondary_color || '#1e40af',
      } as React.CSSProperties}
    >
      {/* Dynamic Venue Styling */}
      <style>{`
        .venue-primary { color: var(--venue-primary) !important; }
        .venue-bg-primary { background-color: var(--venue-primary) !important; }
        .venue-border-primary { border-color: var(--venue-primary) !important; }
        .venue-secondary { color: var(--venue-secondary) !important; }
        .venue-bg-secondary { background-color: var(--venue-secondary) !important; }
        .venue-gradient {
          background: linear-gradient(135deg, var(--venue-primary), var(--venue-secondary)) !important;
        }
        .venue-primary-glow {
          color: var(--venue-primary) !important;
          text-shadow: 0 0 10px var(--venue-primary) !important;
        }
      `}</style>

      {/* Hero Section - now supports using the venue image as a full-bleed background cover */}
      <div
        className="text-white py-12 md:py-16 px-4 md:px-6 relative"
        style={
          venue?.logo_url
            ? {
                backgroundImage: `url(${venue.logo_url})`,
                backgroundSize: 'cover',
                backgroundRepeat: "no-repeat",
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        {/* Overlay for readability when a background image is present */}
        {venue?.logo_url && <div className="absolute inset-0 bg-black/40" />}

        <div className="responsive-container text-center space-y-4 md:space-y-6 relative z-10">
          {/* Small demo banner (dismissible) */}
          {demo && showDemoBanner && (
            <div className="fixed top-14 left-1/2 -translate-x-1/2 z-50">
              <div className="bg-yellow-500/95 text-black px-4 py-2 rounded shadow">DEMO MODE — actions are simulated. <button className="underline ml-3" onClick={() => setShowDemoBanner(false)}>Dismiss</button></div>
            </div>
          )}

          {/* Decorative mic/avatar */}
          <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto animate-pulse-beat">
            <Mic className="h-12 w-12" />
          </div>

          <h1 className="text-5xl md:text-7xl font-bold">
            {venueName || (venue_slug ? venue_slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Loading...')}
          </h1>
          <p className="text-xl md:text-2xl text-white/90">Karaoke Night</p>
          <p className="text-lg text-white/80">{isBasic ? 'Welcome! Request your favorites below.' : 'Welcome! Check the queue and request your favorites below.'}</p>
          <div className="flex justify-center gap-8 text-center">
            {!isBasic && (
              <div>
                <div className="text-3xl font-bold">{songs.length}</div>
                <div className="text-white/80">Songs Available</div>
              </div>
            )}
            <div>
              <div className="text-3xl font-bold">{activeQueue.length}</div>
              <div className="text-white/80">In Queue</div>
            </div>
            <div>
              <div className="text-3xl font-bold">{activeQueue.length * 4}min</div>
              <div className="text-white/80">Est. Wait</div>
            </div>
          </div>
        </div>
      </div>

    

      {/* Banner Advertisement */}
      {showBanner && venue?.welcome_message && !isBasic && (
        <div className="bg-gradient-to-r from-primary/20 to-secondary/20 border-b border-primary/30">
          <div className="responsive-container py-4">
            <div className="flex items-center justify-between bg-card/50 backdrop-blur-sm rounded-lg p-4 border border-primary/20">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                  <Heart className="h-4 w-4 venue-primary-glow" />
                </div>
                <p className="text-sm md:text-base text-foreground font-medium">
                  {venue.welcome_message}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowBanner(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                ✕
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="responsive-container space-y-6 md:space-y-8">
        {/* Live Queue - Priority Display at Top */}
        <Card className="card-neon mt-10 venue-border-primary border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Users className="h-6 w-6 venue-primary" />
              🔴 LIVE Queue ({activeQueue.length} waiting)
              {activeQueue.length > 0 && (
                <Badge className="bg-primary/20 venue-primary-glow animate-pulse">
                  LIVE
                </Badge>
              )}
            </CardTitle>
            <CardDescription>Real-time queue - your position updates automatically!</CardDescription>
          </CardHeader>
          <CardContent>
            {activeQueue.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Mic className="h-16 w-16 mx-auto mb-6 opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No one in queue yet!</h3>
                <p>Be the first to request a song and get the party started! 🎉</p>
              </div>
            ) : (
              <div className="space-y-4">
                {activeQueue.slice(0, 5).map((request, index) => (
                  <div 
                    key={request.id} 
                    className={`flex items-center space-x-4 p-6 border rounded-lg transition-all duration-300 ${
                      index === 0 
                        ? 'bg-primary/10 border-primary-glow border-2 animate-pulse shadow-lg shadow-primary/20' 
                        : 'bg-card/50 border-border hover:bg-card/70'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                      index === 0 ? 'bg-gradient-primary animate-pulse' : 'bg-gradient-secondary'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-xl">{request.songs?.title || request.custom_song_title || 'Custom Song'}</h4>
                      <p className="text-lg text-muted-foreground">{request.songs?.artist || request.custom_song_artist || 'Unknown Artist'}</p>
                      <p className="text-xl text-primary font-medium">
                        {index === 0 ? '🎤 NOW SINGING: ' : '🎵 '}{request.singer_name}
                      </p>
                    </div>
                    {index === 0 && (
                      <Badge className="bg-gradient-primary text-white font-bold animate-bounce">
                        ON STAGE
                      </Badge>
                    )}
                  </div>
                ))}
                {activeQueue.length > 5 && (
                  <p className="text-center text-muted-foreground pt-4 text-lg">
                    +{activeQueue.length - 5} more singers waiting...
                  </p>
                )}
                
                <div className="mt-6 p-6 bg-primary/10 rounded-lg border border-primary/20 text-center">
                  <div className="flex items-center justify-center gap-2 text-xl font-medium">
                    <Clock className="h-6 w-6 text-primary" />
                    <span className="text-primary">Estimated wait time: {activeQueue.length * 4} minutes</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Search Section - HIDDEN FOR BASIC TIER */}
        {!isBasic && (
        <Card className="card-neon">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Search className="h-6 w-6 text-primary" />
              Find Your Song
            </CardTitle>
            <CardDescription>Search through our extensive karaoke collection</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search by song title or artist..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-glow pl-12 text-lg py-6"
              />
            </div>


          </CardContent>
        </Card>
        )}

        {/* Song Library - HIDDEN FOR BASIC TIER */}
        {!isBasic && (
        <Card className="card-neon">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <Music className="h-5 w-5 text-primary" />
              Song Library
              <div className="flex gap-2">
                <Badge className="bg-primary/20 text-primary-glow">
                  {filteredSongs.length} {searchTerm ? 'found' : 'songs'}
                </Badge>
                {searchTerm && songs.length !== filteredSongs.length && (
                  <Badge variant="outline" className="text-muted-foreground">
                    of {songs.length} total
                  </Badge>
                )}
                {songs.length >= 10000 && (
                  <Badge className="bg-green-500/20 text-green-400">
                    Large Library
                  </Badge>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="space-y-3 max-h-[70vh] overflow-y-auto"
              onScroll={(e) => {
                const element = e.target as HTMLDivElement;
                const bottom = element.scrollHeight - element.scrollTop - element.clientHeight;
                // Load more songs when user scrolls near bottom (within 500px)
                if (bottom < 500 && displayedSongsCount < filteredSongs.length) {
                  setDisplayedSongsCount(prev => Math.min(prev + 50, filteredSongs.length));
                }
              }}
            >
              {filteredSongs.slice(0, displayedSongsCount).map((song) => (
                <div key={song.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 p-3 sm:p-4 border border-border rounded-lg bg-card/50 hover:bg-card/70 transition-all duration-300">
                  <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <Music className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-base sm:text-lg truncate">{song.title}</h4>
                      <p className="text-muted-foreground text-sm truncate">{song.artist}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                          {song.genre}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => openRequestDialog(song)}
                    className="btn-karaoke w-full sm:w-auto flex-shrink-0"
                    size="sm"
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Request
                  </Button>
                </div>
              ))}
              
              {displayedSongsCount < filteredSongs.length && (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground">Showing {displayedSongsCount} of {filteredSongs.length} songs • Scroll to load more</p>
                </div>
              )}
              
              {filteredSongs.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Music className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No songs found</p>
                  <p>Try a different search term</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Custom Song Request Section */}
        {isBasic ? (
          <Card className="card-neon mt-10 border-orange-500/30 bg-orange-500/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-400">
                <Music className="h-5 w-5" />
                Request a Song
              </CardTitle>
              <CardDescription>
               Request a custom song and we'll add it to the queue for host approval
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="custom-title">Song Title</Label>
                  <Input
                    id="custom-title"
                    value={customSongTitle}
                    onChange={(e) => setCustomSongTitle(e.target.value)}
                    placeholder="Enter song title"
                    className="input-glow"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="custom-artist">Artist</Label>
                  <Input
                    id="custom-artist"
                    value={customSongArtist}
                    onChange={(e) => setCustomSongArtist(e.target.value)}
                    placeholder="Enter artist name"
                    className="input-glow"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="custom-singer">Your Name</Label>
                  <Input
                    id="custom-singer"
                    value={customSingerName}
                    onChange={(e) => setCustomSingerName(e.target.value)}
                    placeholder="Enter your name"
                    className="input-glow"
                  />
                </div>
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                  <p className="text-sm text-orange-400">
                    ⏳ Custom songs require host approval before being added to the queue
                  </p>
                </div>
                <Button
                  onClick={handleCustomRequest}
                  disabled={submitting}
                  className="w-full btn-karaoke"
                >
                  <Music className="mr-2 h-4 w-4" />
                  {submitting ? "Submitting..." : "Submit Request"}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
        <Card className="card-neon border-orange-500/30 bg-orange-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-400">
              <Music className="h-5 w-5" />
              Can't Find Your Song?
            </CardTitle>
            <CardDescription>
              Request a custom song that's not in our library and we'll add it to the queue for approval
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <Dialog open={isCustomRequestOpen} onOpenChange={setIsCustomRequestOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="btn-karaoke border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                  >
                    <Music className="mr-2 h-4 w-4" />
                    Request Custom Song
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Request a Custom Song</DialogTitle>
                    <DialogDescription>
                      Tell us what song you'd like to sing and we'll add it to the queue for host approval
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="custom-title">Song Title</Label>
                      <Input
                        id="custom-title"
                        value={customSongTitle}
                        onChange={(e) => setCustomSongTitle(e.target.value)}
                        placeholder="Enter song title"
                        className="input-glow"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="custom-artist">Artist</Label>
                      <Input
                        id="custom-artist"
                        value={customSongArtist}
                        onChange={(e) => setCustomSongArtist(e.target.value)}
                        placeholder="Enter artist name"
                        className="input-glow"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="custom-singer">Your Name</Label>
                      <Input
                        id="custom-singer"
                        value={customSingerName}
                        onChange={(e) => setCustomSingerName(e.target.value)}
                        placeholder="Enter your name"
                        className="input-glow"
                      />
                    </div>
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                      <p className="text-sm text-orange-400">
                        ⏳ Custom songs require host approval before being added to the queue
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleCustomRequest}
                        disabled={submitting}
                        className="flex-1 btn-karaoke"
                      >
                        {submitting ? "Submitting..." : "Submit Request"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setIsCustomRequestOpen(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
        )}

      </div>

      {/* Request Dialog */}
      <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
        <DialogContent className="card-neon border-primary/20">
          <DialogHeader>
            <DialogTitle className="text-2xl">Request Song</DialogTitle>
            <DialogDescription>
              Enter your name to add this song to the queue
            </DialogDescription>
          </DialogHeader>
          
          {selectedSong && (
            <div className="space-y-6">
              <div className="flex items-center space-x-4 p-4 bg-primary/10 rounded-lg border border-primary/20">
                <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                  <Music className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg">{selectedSong.title}</h4>
                  <p className="text-muted-foreground">{selectedSong.artist}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="singer-name">Your Name</Label>
                <Input
                  id="singer-name"
                  placeholder="Enter your name"
                  value={singerName}
                  onChange={(e) => setSingerName(e.target.value)}
                  className="input-glow"
                />
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={handleRequestSong}
                  className="btn-karaoke flex-1"
                  disabled={demo || !singerName.trim()}
                >
                  <Heart className="mr-2 h-4 w-4" />
                  Add to Queue
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setIsRequestDialogOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SingerPage;